import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { CartItem } from '../store/usePosStore';
import type { CartEvaluationResult, PromotionWithTargets } from '../../promotions/domain/promotionTypes';
import { listPromotionsCache } from '../../promotions/infra/promotionsRepoRead';
import { usePromotionsReloadBus } from '../../promotions/public-api';
import { normalizePromoCode } from '../../promotions/domain/cartEvaluationOptions';
import {
  fetchPromotionUsageCounts,
  promotionIdsNeedingUsageCount,
} from '../../promotions/infra/promotionUsageCounts';
import { usePlanEntitlements } from '../../shell/providers/PlanEntitlementsProvider'
import { useGlobalFeatureFlags } from '../../shell/providers/GlobalFeatureFlagsProvider'
import { isGlobalFeatureOn } from '../../shell/feature-flags/globalFeatureFlagsService'
import { planAllowsPromotions } from '../../shell/feature-flags/planEntitlementsService';
import { showInfo, showSuccess } from '../../shared/ui/appToast';
import {
  evaluatePosCartPromotions,
  findPromotionByCode,
  listCashierSelectablePromotions,
  listInactiveCashierPromoHints,
  validatePromoCodeForCart,
} from './posCartPromotions';
import {
  resolveCartPromoUiStatus,
  STALE_PROMO_RESTORE_TOAST,
  type CartPromoUiStatus,
} from './posCartPromoPresentation';
import { sumDiscretionaryDiscount } from './supervisorDiscountPolicy';

/** Mensajes al estilo Shopify/Square checkout (ES). */
const PROMO_MSG = {
  empty: 'Ingres\u00e1 un c\u00f3digo de descuento',
  alreadyApplied: 'Este c\u00f3digo ya est\u00e1 aplicado',
  cannotCombine: 'Este c\u00f3digo no se puede combinar con los descuentos actuales',
  applied: 'C\u00f3digo aplicado',
  removed: 'C\u00f3digo eliminado',
  removedNamed: (code: string) => `Se quit\u00f3 el c\u00f3digo ${code}`,
  cashierApplied: 'Promoci\u00f3n aplicada',
  cashierRemoved: 'Promoci\u00f3n eliminada',
  allCleared: 'Promociones eliminadas',
} as const;

const PROMO_TOAST_TITLE = 'Promoci\u00f3n';

export function usePosCartPromotions(
  cart: CartItem[],
  branchId: string | null | undefined,
  canDiscount = true
) {
  const { entitlements } = usePlanEntitlements();
  const { flags: globalFeatureFlags } = useGlobalFeatureFlags();

  const promotionsEnabled = useMemo(
    () =>
      isGlobalFeatureOn(globalFeatureFlags, 'promotions_integrations') &&
      planAllowsPromotions(entitlements),
    [entitlements, globalFeatureFlags]
  );

  const [promotions, setPromotions] = useState<PromotionWithTargets[]>([]);
  const [usageCountByPromotionId, setUsageCountByPromotionId] = useState<Map<string, number>>(
    () => new Map(),
  );
  const [loading, setLoading] = useState(false);
  const [promotionsReloadNonce, setPromotionsReloadNonce] = useState(0);
  const busNonce = usePromotionsReloadBus((s) => s.nonce);
  const [promoCodeInput, setPromoCodeInput] = useState('');
  const [appliedPromoCodes, setAppliedPromoCodes] = useState<string[]>([]);
  const [promoCodeError, setPromoCodeError] = useState<string | null>(null);
  const [selectedCashierPromotionId, setSelectedCashierPromotionId] = useState<string | null>(null);
  const [isApplying, setIsApplying] = useState(false);
  const [isClearing, setIsClearing] = useState(false);
  const reconcileAfterRestoreRef = useRef(false);
  const isApplyingRef = useRef(false);
  const isClearingRef = useRef(false);

  /** P1-05: reloj vivo mientras hay carrito — evita freeze mid-cart en endsAt/schedule. */
  const [evaluationNowMs, setEvaluationNowMs] = useState(() => Date.now());
  useEffect(() => {
    if (!promotionsEnabled || cart.length === 0) return;
    setEvaluationNowMs(Date.now());
    const id = setInterval(() => setEvaluationNowMs(Date.now()), 15_000);
    return () => clearInterval(id);
  }, [promotionsEnabled, cart.length]);
  const evaluationNow = useMemo(() => new Date(evaluationNowMs), [evaluationNowMs]);

  const appliedPromoCode = appliedPromoCodes[0] ?? null;

  const setAppliedPromoCode = useCallback((code: string | null) => {
    const normalized = normalizePromoCode(code);
    setAppliedPromoCodes(normalized ? [normalized] : []);
  }, []);

  const reloadPromotionsFromLocal = useCallback(() => {
    setPromotionsReloadNonce((nonce) => nonce + 1);
  }, []);

  useEffect(() => {
    if (!promotionsEnabled || !branchId) {
      setPromotions([]);
      setUsageCountByPromotionId(new Map());
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    void listPromotionsCache()
      .then(async (rows) => {
        if (cancelled) return;
        setPromotions(rows);
        const limitedIds = promotionIdsNeedingUsageCount(rows);
        if (limitedIds.length === 0) {
          setUsageCountByPromotionId(new Map());
          return;
        }
        try {
          const counts = await fetchPromotionUsageCounts(limitedIds);
          if (!cancelled) setUsageCountByPromotionId(counts);
        } catch (error) {
          console.warn('[usePosCartPromotions] No se pudieron cargar usage counts', error);
          // Fail-open: sin conteo el motor trata usageLimit como 0 usos (sigue aplicando).
          // Preferible a bloquear todas las promos si el RPC falla.
          if (!cancelled) setUsageCountByPromotionId(new Map());
        }
      })
      .catch((error) => {
        console.warn('[usePosCartPromotions] No se pudieron cargar promociones locales', error);
        if (!cancelled) {
          setPromotions([]);
          setUsageCountByPromotionId(new Map());
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [promotionsEnabled, branchId, promotionsReloadNonce, busNonce]);

  useEffect(() => {
    if (cart.length === 0) {
      setAppliedPromoCodes([]);
      setPromoCodeInput('');
      setPromoCodeError(null);
      setSelectedCashierPromotionId(null);
    }
  }, [cart.length]);

  const evaluationOptions = useMemo(() => {
    const selectedPromotionIds = selectedCashierPromotionId ? [selectedCashierPromotionId] : undefined;
    const hasManual =
      appliedPromoCodes.length > 0 || Boolean(selectedPromotionIds?.length);
    const hasUsage = usageCountByPromotionId.size > 0;
    if (!hasManual && !hasUsage) {
      return undefined;
    }
    return {
      ...(hasManual
        ? {
            promoCodes: appliedPromoCodes,
            promoCode: appliedPromoCodes[0] ?? null,
            selectedPromotionIds,
          }
        : {}),
      ...(hasUsage ? { usageCountByPromotionId } : {}),
    };
  }, [appliedPromoCodes, selectedCashierPromotionId, usageCountByPromotionId]);

  const evaluation: CartEvaluationResult | null = useMemo(() => {
    if (!promotionsEnabled || !branchId || cart.length === 0) {
      return null;
    }
    return evaluatePosCartPromotions({
      cart,
      promotions,
      branchId,
      options: evaluationOptions,
      now: evaluationNow,
    });
  }, [promotionsEnabled, branchId, cart, promotions, evaluationOptions, evaluationNow]);

  const cashierEligiblePromotions = useMemo(() => {
    if (!promotionsEnabled || !branchId || cart.length === 0) return [];
    return listCashierSelectablePromotions(
      cart,
      promotions,
      branchId,
      evaluationNow,
      usageCountByPromotionId,
    );
  }, [promotionsEnabled, branchId, cart, promotions, usageCountByPromotionId, evaluationNow]);

  const inactiveCashierPromoHints = useMemo(() => {
    if (!promotionsEnabled || !branchId) return [];
    return listInactiveCashierPromoHints(promotions, branchId, evaluationNow);
  }, [promotionsEnabled, branchId, promotions, evaluationNow]);

  const promoUiStatus: CartPromoUiStatus = useMemo(
    () =>
      resolveCartPromoUiStatus({
        cart,
        branchId,
        promotions,
        appliedPromoCode,
        selectedCashierPromotionId,
        evaluation,
        now: evaluationNow,
      }),
    [cart, branchId, promotions, appliedPromoCode, selectedCashierPromotionId, evaluation, evaluationNow]
  );

  const applyStaleReconcilePatch = useCallback(() => {
    const patch = promoUiStatus.reconcilePatch;
    if (!patch) return false;

    if (patch.clearAppliedPromoCode) {
      setAppliedPromoCodes([]);
      setPromoCodeInput('');
    }
    if (patch.clearSelectedCashierPromotionId) {
      setSelectedCashierPromotionId(null);
    }
    if (patch.promoCodeError) {
      setPromoCodeError(patch.promoCodeError);
    }
    return true;
  }, [promoUiStatus.reconcilePatch]);

  useEffect(() => {
    if (!promotionsEnabled || !branchId || cart.length === 0 || loading) return;
    const notifyOnRestore = reconcileAfterRestoreRef.current;
    reconcileAfterRestoreRef.current = false;
    const patched = applyStaleReconcilePatch();
    if (patched && notifyOnRestore) {
      showInfo('Promoci\u00f3n', STALE_PROMO_RESTORE_TOAST);
    }
  }, [
    applyStaleReconcilePatch,
    branchId,
    cart.length,
    loading,
    promotions,
    promotionsReloadNonce,
    promoUiStatus.hasStaleSelection,
  ]);

  const hasManualCodePromotions = useMemo(
    () => promotions.some((promo) => promo.applyMode === 'manual_code' && promo.isActive && !promo.deletedAt),
    [promotions]
  );

  const hasCashierSelectPromotions = useMemo(
    () =>
      promotions.some((promo) => promo.applyMode === 'cashier_select' && promo.isActive && !promo.deletedAt),
    [promotions]
  );

  const applyPromoCode = useCallback(() => {
    if (!branchId) return;
    if (isApplyingRef.current) return;
    isApplyingRef.current = true;
    setIsApplying(true);
    try {
    const code = normalizePromoCode(promoCodeInput);
    if (!code) {
      setPromoCodeError(PROMO_MSG.empty);
      return;
    }
    if (appliedPromoCodes.includes(code)) {
      setPromoCodeError(PROMO_MSG.alreadyApplied);
      return;
    }

    const result = validatePromoCodeForCart(
      code,
      cart,
      promotions,
      branchId,
      new Date(),
      usageCountByPromotionId,
    );
    if (!result.ok) {
      setPromoCodeError(result.message);
      return;
    }

    const nextCodes = [...appliedPromoCodes, code];
    const before = evaluatePosCartPromotions({
      cart,
      promotions,
      branchId,
      options: {
        promoCodes: appliedPromoCodes,
        selectedPromotionIds: selectedCashierPromotionId ? [selectedCashierPromotionId] : undefined,
        usageCountByPromotionId,
      },
    });
    const after = evaluatePosCartPromotions({
      cart,
      promotions,
      branchId,
      options: {
        promoCodes: nextCodes,
        selectedPromotionIds: selectedCashierPromotionId ? [selectedCashierPromotionId] : undefined,
        usageCountByPromotionId,
      },
    });
    const promoApplied =
      after != null &&
      (after.cartDiscounts.some((d) => d.promotionId === result.promotion.id) ||
        after.lines.some((line) => line.appliedDiscounts.some((d) => d.promotionId === result.promotion.id)));
    const discountImproved = (after?.discountTotal ?? 0) > (before?.discountTotal ?? 0);

    if (!promoApplied || !discountImproved) {
      setPromoCodeError(PROMO_MSG.cannotCombine);
      return;
    }

    setAppliedPromoCodes(nextCodes);
    setPromoCodeInput('');
    setPromoCodeError(null);
    showSuccess(PROMO_TOAST_TITLE, PROMO_MSG.applied);
    } finally {
      isApplyingRef.current = false;
      setIsApplying(false);
    }
  }, [
    appliedPromoCodes,
    branchId,
    cart,
    promoCodeInput,
    promotions,
    selectedCashierPromotionId,
    usageCountByPromotionId,
  ]);

  const clearAppliedPromoCode = useCallback((code?: string | null, options?: { silent?: boolean }) => {
    if (isClearingRef.current) return;
    isClearingRef.current = true;
    setIsClearing(true);
    try {
    const silent = options?.silent === true;
    if (code) {
      const normalized = normalizePromoCode(code);
      if (!normalized || !appliedPromoCodes.includes(normalized)) return;
      setAppliedPromoCodes((prev) => prev.filter((item) => item !== normalized));
      if (!silent) {
        showInfo(PROMO_TOAST_TITLE, PROMO_MSG.removedNamed(normalized));
      }
    } else {
      const count = appliedPromoCodes.length;
      if (count === 0) {
        setPromoCodeInput('');
        setPromoCodeError(null);
        return;
      }
      setAppliedPromoCodes([]);
      if (!silent) {
        showInfo(PROMO_TOAST_TITLE, count === 1 ? PROMO_MSG.removed : PROMO_MSG.allCleared);
      }
    }
    setPromoCodeInput('');
    setPromoCodeError(null);
    } finally {
      isClearingRef.current = false;
      setIsClearing(false);
    }
  }, [appliedPromoCodes]);

  const selectCashierPromotion = useCallback(
    (promotionId: string | null) => {
      if (selectedCashierPromotionId === promotionId) {
        setPromoCodeError(null);
        return;
      }
      if (promotionId) {
        showSuccess(PROMO_TOAST_TITLE, PROMO_MSG.cashierApplied);
      } else if (selectedCashierPromotionId) {
        showInfo(PROMO_TOAST_TITLE, PROMO_MSG.cashierRemoved);
      }
      setSelectedCashierPromotionId(promotionId);
      setPromoCodeError(null);
    },
    [selectedCashierPromotionId]
  );

  const clearPromoSelection = useCallback(() => {
    const hadManual = appliedPromoCodes.length > 0;
    const hadCashier = Boolean(selectedCashierPromotionId);
    clearAppliedPromoCode(null, { silent: true });
    setSelectedCashierPromotionId(null);
    setPromoCodeError(null);
    if (hadManual || hadCashier) {
      showInfo(PROMO_TOAST_TITLE, PROMO_MSG.allCleared);
    }
  }, [appliedPromoCodes.length, clearAppliedPromoCode, selectedCashierPromotionId]);

  const reconcileStalePromoAfterRestore = useCallback(() => {
    reconcileAfterRestoreRef.current = true;
    reloadPromotionsFromLocal();
  }, [reloadPromotionsFromLocal]);

  const appliedManualPromotionIds = useMemo(() => {
    return appliedPromoCodes
      .map((code) => findPromotionByCode(code, promotions)?.id ?? null)
      .filter((id): id is string => Boolean(id));
  }, [appliedPromoCodes, promotions]);

  /** Compat: primer c\u00f3digo aplicado (persistencia / UI legacy). */
  const appliedManualPromotionId = appliedManualPromotionIds[0] ?? null;

  const showPromoActions =
    promotionsEnabled &&
    canDiscount &&
    cart.length > 0 &&
    (hasManualCodePromotions || hasCashierSelectPromotions);

  const discretionaryDiscountTotal = useMemo(
    () => sumDiscretionaryDiscount(evaluation, promotions),
    [evaluation, promotions]
  );

  return {
    promotionsEnabled,
    loading,
    isApplying,
    isClearing,
    evaluation,
    promoCodeInput,
    setPromoCodeInput,
    appliedPromoCode,
    appliedPromoCodes,
    setAppliedPromoCode,
    appliedManualPromotionId,
    appliedManualPromotionIds,
    promoCodeError,
    applyPromoCode,
    clearAppliedPromoCode,
    selectedCashierPromotionId,
    selectCashierPromotion,
    cashierEligiblePromotions,
    inactiveCashierPromoHints,
    clearPromoSelection,
    promoUiStatus,
    reloadPromotionsFromLocal,
    reconcileStalePromoAfterRestore,
    showPromoActions,
    hasManualCodePromotions,
    hasCashierSelectPromotions,
    discretionaryDiscountTotal,
  };
}
